import React, { useEffect } from 'react';
import Button from '../common/Button';
import Badge from '../common/Badge';

const UserViewModal = ({ user, onClose }) => {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  if (!user) return null;

  const userRoles = Array.isArray(user.roles) ? user.roles.map(r => r.toLowerCase()) : [];
  const modules = [
    { key: 'daily_business', label: 'Daily Business' },
    { key: 'stock_check', label: 'Stock Check' },
    { key: 'liter_statement', label: 'Liter Statement' }
  ];

  const permissions = [
    { key: 'create', label: 'Create' },
    { key: 'edit', label: 'Update' },
    { key: 'view', label: 'View' },
    { key: 'delete', label: 'Delete' }
  ];

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))] overflow-y-auto"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="user-view-modal-title"
    >
      <div
        className="bg-white dark:bg-slate-800 rounded-2xl sm:rounded-3xl p-6 sm:p-8 max-w-4xl w-full shadow-2xl border border-slate-100 dark:border-slate-700 slide-in my-8 max-h-dvh-90 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4 sm:mb-6">
          <h2 id="user-view-modal-title" className="text-2xl sm:text-3xl font-bold gradient-text flex items-center gap-2 sm:gap-3">
            <span className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-primary to-accent rounded-xl flex items-center justify-center shrink-0">
              <i className="fas fa-user text-white text-sm sm:text-base" aria-hidden="true"></i>
            </span>
            User Details
          </h2>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-white transition-all duration-300 flex items-center justify-center btn-hover-scale shrink-0"
            aria-label="Close"
          >
            <i className="fas fa-times" aria-hidden="true"></i>
          </button>
        </div>

        <div className="space-y-4 sm:space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gradient-to-r from-primary/10 to-accent/10 dark:from-primary/15 dark:to-accent/15 rounded-xl p-4 border-2 border-primary/20 dark:border-primary/30">
              <p className="text-xs sm:text-sm font-semibold text-slate-500 dark:text-slate-400 mb-2 flex items-center gap-2">
                <i className="fas fa-user text-xs sm:text-sm" aria-hidden="true"></i> Name
              </p>
              <div className="text-base sm:text-lg font-bold text-slate-800 dark:text-slate-100">
                {user.name || '-'}
              </div>
            </div>

            <div className="bg-gradient-to-r from-primary/10 to-accent/10 dark:from-primary/15 dark:to-accent/15 rounded-xl p-4 border-2 border-primary/20 dark:border-primary/30">
              <p className="text-xs sm:text-sm font-semibold text-slate-500 dark:text-slate-400 mb-2 flex items-center gap-2">
                <i className="fas fa-phone text-xs sm:text-sm" aria-hidden="true"></i> Phone Number
              </p>
              <div className="text-base sm:text-lg font-bold text-slate-800 dark:text-slate-100">
                {user.phone || '-'}
              </div>
            </div>

            <div className="bg-gradient-to-r from-primary/10 to-accent/10 dark:from-primary/15 dark:to-accent/15 rounded-xl p-4 border-2 border-primary/20 dark:border-primary/30">
              <p className="text-xs sm:text-sm font-semibold text-slate-500 dark:text-slate-400 mb-2 flex items-center gap-2">
                <i className="fas fa-store text-xs sm:text-sm" aria-hidden="true"></i> Shop Name
              </p>
              <div className="text-base sm:text-lg font-bold text-slate-800 dark:text-slate-100">
                {user.shop_name || <span className="text-slate-400 dark:text-slate-500 italic">Not set</span>}
              </div>
            </div>

            <div className="bg-gradient-to-r from-primary/10 to-accent/10 dark:from-primary/15 dark:to-accent/15 rounded-xl p-4 border-2 border-primary/20 dark:border-primary/30">
              <p className="text-xs sm:text-sm font-semibold text-slate-500 dark:text-slate-400 mb-2 flex items-center gap-2">
                <i className="fas fa-barcode text-xs sm:text-sm" aria-hidden="true"></i> Shop Code
              </p>
              <div className="text-base sm:text-lg font-bold text-slate-800 dark:text-slate-100">
                {user.shop_code || <span className="text-slate-400 dark:text-slate-500 italic">Not set</span>}
              </div>
            </div>
          </div>

          {/* Roles */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-500/10 dark:to-indigo-500/10 rounded-xl p-4 border-2 border-blue-200 dark:border-blue-500/30">
            <p className="text-sm font-semibold text-slate-500 dark:text-slate-400 mb-3 flex items-center gap-2">
              <i className="fas fa-user-tag" aria-hidden="true"></i> Roles
            </p>
            <div className="flex gap-2 flex-wrap">
              {userRoles.length > 0 ? (
                userRoles.map((role, idx) => (
                  <Badge key={idx} color="primary" className="capitalize">{role}</Badge>
                ))
              ) : (
                <span className="text-slate-400 dark:text-slate-500 italic">No roles assigned</span>
              )}
            </div>
          </div>

          {/* Password Status */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-emerald-500/10 dark:to-emerald-500/10 rounded-xl p-4 border-2 border-green-200 dark:border-emerald-500/30">
            <p className="text-sm font-semibold text-slate-500 dark:text-slate-400 mb-3 flex items-center gap-2">
              <i className="fas fa-key" aria-hidden="true"></i> Password Status
            </p>
            <div className="space-y-2">
              {userRoles.includes('admin') && (
                <div className="flex items-center justify-between bg-white dark:bg-slate-800 rounded-lg p-3 border border-slate-200 dark:border-slate-600">
                  <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Admin Password</span>
                  <Badge color={user.passwordStatus?.admin ? 'success' : 'gray'}>{user.passwordStatus?.admin ? 'Set' : 'Not Set'}</Badge>
                </div>
              )}
              {userRoles.includes('manager') && (
                <div className="flex items-center justify-between bg-white dark:bg-slate-800 rounded-lg p-3 border border-slate-200 dark:border-slate-600">
                  <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Manager Password</span>
                  <Badge color={user.passwordStatus?.manager ? 'success' : 'gray'}>{user.passwordStatus?.manager ? 'Set' : 'Not Set'}</Badge>
                </div>
              )}
              {userRoles.includes('staff') && (
                <div className="flex items-center justify-between bg-white dark:bg-slate-800 rounded-lg p-3 border border-slate-200 dark:border-slate-600">
                  <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Staff Password</span>
                  <Badge color={user.passwordStatus?.staff ? 'success' : 'gray'}>{user.passwordStatus?.staff ? 'Set' : 'Not Set'}</Badge>
                </div>
              )}
              {userRoles.length === 0 && (
                <div className="text-sm text-slate-500 dark:text-slate-400 italic">No roles assigned, so no passwords configured</div>
              )}
            </div>
            <div className="mt-3 p-2 bg-yellow-50 dark:bg-amber-500/10 border border-yellow-200 dark:border-amber-500/30 rounded-lg">
              <p className="text-xs text-yellow-700 dark:text-amber-400">
                <i className="fas fa-info-circle mr-1" aria-hidden="true"></i>
                Note: Passwords are securely hashed and cannot be displayed. Only password status (set/not set) is shown.
              </p>
            </div>
          </div>

          {/* Permissions */}
          {user.permissions && Object.keys(user.permissions).length > 0 && (
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-500/10 dark:to-pink-500/10 rounded-xl p-4 border-2 border-purple-200 dark:border-purple-500/30">
              <p className="text-sm font-semibold text-slate-500 dark:text-slate-400 mb-3 flex items-center gap-2">
                <i className="fas fa-shield-alt" aria-hidden="true"></i> Permissions
              </p>
              <div className="space-y-4">
                {Object.keys(user.permissions).map(role => (
                  <div key={role} className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-600">
                    <h4 className="text-md font-semibold text-primary dark:text-accent mb-3 capitalize">{role} Permissions</h4>
                    <div className="space-y-3">
                      {modules.map(module => {
                        const modulePermissions = user.permissions[role]?.[module.key] || [];
                        return (
                          <div key={module.key} className="bg-slate-50 dark:bg-slate-900/50 p-3 rounded-lg">
                            <h5 className="font-semibold text-sm text-slate-700 dark:text-slate-200 mb-2">{module.label}</h5>
                            <div className="flex gap-2 flex-wrap">
                              {permissions.map(permission => {
                                const hasPermission = modulePermissions.includes(permission.key);
                                return (
                                  <Badge key={permission.key} color={hasPermission ? 'success' : 'gray'}>
                                    {permission.label}
                                  </Badge>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Account Status */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-500/10 dark:to-indigo-500/10 rounded-xl p-4 border-2 border-blue-200 dark:border-blue-500/30">
              <p className="text-xs sm:text-sm font-semibold text-slate-500 dark:text-slate-400 mb-2 flex items-center gap-2">
                <i className="fas fa-calendar-plus text-xs sm:text-sm" aria-hidden="true"></i> Created At
              </p>
              <div className="text-sm font-bold text-slate-800 dark:text-slate-100">
                {user.created_at ? new Date(user.created_at).toLocaleString() : '-'}
              </div>
            </div>

            {user.updated_at && (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-500/10 dark:to-indigo-500/10 rounded-xl p-4 border-2 border-blue-200 dark:border-blue-500/30">
                <p className="text-xs sm:text-sm font-semibold text-slate-500 dark:text-slate-400 mb-2 flex items-center gap-2">
                  <i className="fas fa-calendar-edit text-xs sm:text-sm" aria-hidden="true"></i> Last Updated
                </p>
                <div className="text-sm font-bold text-slate-800 dark:text-slate-100">
                  {new Date(user.updated_at).toLocaleString()}
                </div>
              </div>
            )}

            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-500/10 dark:to-indigo-500/10 rounded-xl p-4 border-2 border-blue-200 dark:border-blue-500/30">
              <p className="text-xs sm:text-sm font-semibold text-slate-500 dark:text-slate-400 mb-2 flex items-center gap-2">
                <i className="fas fa-toggle-on text-xs sm:text-sm" aria-hidden="true"></i> Account Status
              </p>
              <Badge color={user.is_active ? 'success' : 'danger'}>{user.is_active ? 'Active' : 'Inactive'}</Badge>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 sm:gap-4 mt-6 sm:mt-8">
          <Button variant="secondary" fullWidth className="sm:w-auto" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UserViewModal;
